import React, { useEffect, useState } from "react";
import "../profile.css";
import MAvatar from "../../../components/ui/atoms/MAvatar";
import MBadge from "../../../components/ui/atoms/MBadge";
import { useNavigate } from "react-router-dom";
import { NavigationButton } from "@/components/ui/atoms/MNavigationButton";
import { useProfileNavigation } from "@/config/navigation/profile-navigation";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { useFamily } from "@/hooks/useFamily";
import { useDeleteMemberAvatar } from "@/hooks/useDeleteMemberAvatar";
import { useAvatar } from "@/hooks/useAvatar";
import { MDialog } from "@/components/ui/atoms/MDialog";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import MTopBar from "@/components/ui/atoms/MTopBar";
import { useTranslation } from "react-i18next";
import { memberNormalizer } from '@/utils/memberNormalizer';
import { trackUserDataLoaded } from '@/utils/gtm';
import { useNavigationSource } from '@/hooks/useNavigationSource';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const BasicInformation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateBackIfExists, hasNavigationSource, getNavigationSource } = useNavigationSource();
  const [show, setShow] = useState(false);
  const { member, selectedMember } = useSelector((state: RootState) => ({
    member: state.auth?.member,
    selectedMember: state.auth?.selectedMember,
  }));
  const { data: familyMembers = [] } = useFamily();
  const displayMember = selectedMember || member;
  const memberInfo = getMemberInfo();

  // Reload page when displayMember.id changes (robust, id only)
  // const prevDisplayMemberId = React.useRef<string | undefined>(displayMember?.id);
  // useEffect(() => {
  //   if (prevDisplayMemberId.current && displayMember?.id && displayMember.id !== prevDisplayMemberId.current) {
  //     window.location.reload();
  //   }
  //   prevDisplayMemberId.current = displayMember?.id;
  // }, [displayMember?.id]);

  // Avatar logic (shared hook)
  const { avatarUrl, reloadAvatar } = useAvatar();

  // (extractFileKey moved to useMedia)

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { deleteAvatar, deleting, deleteError } = useDeleteMemberAvatar();

  const handleBack = () => {
    const didNavigate = navigateBackIfExists();
    if (!didNavigate) {
      navigate('/profile');
    }
  };

  // Delete avatar handler (calls hook and updates local state)
  const handleDeleteAvatar = async () => {
    const success = await deleteAvatar(displayMember.id, avatarUrl);
    if (success) {
      await reloadAvatar();
      setShowDeleteDialog(false);
    }
  };

  useEffect(() => {
    setShow(true);
  }, []);

  // Track GTM user_data_loaded event
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_EDIT,
    });
  }, [memberInfo?.id]);

  const handleDeleteAccount = async () => {
    // Track delete account click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_DELETE_ACCOUNT,
    });
    
    // Match family index.tsx logic for FO self-delete
    const isSelf = true; // Always self on basic info page
    const isFO = displayMember?.family_member_type === "fo";
    let eligibleForOwnership: any[] = [];
    if (isSelf && isFO && Array.isArray(familyMembers)) {
      eligibleForOwnership = familyMembers.filter(
        (m: any) =>
          m.registered_member &&
          (m.id || m._id) !== (displayMember.id || displayMember._id) &&
          m.family_member_type !== "fo"
      );
    }
    const member = memberNormalizer(displayMember); 
    navigate("/profile/family/delete-member", {
      state: {
        action: "fo-delete-registered",
        targetMember: member,
        eligibleForOwnership,
      },
    });
  };

  return (
    <div
      className={`profile-page-root transition-all duration-500 ease-out ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      <div className="profile-sticky-header">
        <MTopBar hideProfilePicker hideBack style={{ background: '#FBF6FF' }}/>
        <FormHeader
          subHeading="Basic information"
          module="basic-info"
          memberInfo={memberInfo}
          showProfilePickerRow
          showGradient
          hideBack={false}
          onBack={handleBack}
        />
      </div>

      {/* Avatar with edit */}
      <div
        className="profile-avatar-edit-row"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          position: "relative",
        }}
      >
        {displayMember.avatar && avatarUrl ? (
          <MAvatar
            size="xl"
            text={`${displayMember?.first_name || ""}${
              displayMember?.last_name ? " " + displayMember.last_name : ""
            }`.trim()}
            type={"dp"}
            src={avatarUrl}
            className="profile-avatar-lg"
            showBadge
            memberId={displayMember?.id ? String(displayMember.id) : undefined}
          />
        ) : (
          <MAvatar
            size="xl"
            text={`${displayMember?.first_name || ""}${
              displayMember?.last_name ? " " + displayMember.last_name : ""
            }`.trim()}
            type={"no-dp"}
            src=""
            className="profile-avatar-lg"
            showBadge
            memberId={displayMember?.id ? String(displayMember.id) : undefined}
          />
        )}
        {(displayMember?.registered_member || displayMember?.family_member_type === 'fo') && (
          <MBadge variant="purple-inverse">Maatra account</MBadge>
        )}
        {/* Trash icon for avatar delete */}
        {/* {avatarUrl && (
          <button
            type="button"
            aria-label="Delete Avatar"
            className="profile-avatar-delete-btn"
            onClick={() => setShowDeleteDialog(true)}
          >
            <IconTrash size={22} color="#d32f2f" />
          </button>
        )} */}
      </div>

      <MDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Delete Avatar"
        primaryCtaText={deleting ? "Deleting..." : "Delete"}
        onPrimaryCta={handleDeleteAvatar}
        isPrimaryCtaLoading={deleting}
        isPrimaryCtaDisabled={deleting}
        secondaryCtaText="Cancel"
        onSecondaryCta={() => setShowDeleteDialog(false)}
        destructivePrimary
      >
        <div>Are you sure you want to delete your avatar?</div>
        {deleteError && (
          <div style={{ color: "red", marginTop: 8 }}>{deleteError}</div>
        )}
      </MDialog>

      {/* Nav Items (from config, dynamic) */}
      <div className="profile-list flex flex-col gap-0 mt-[32px]">
        {useProfileNavigation().userProfileNav.map((item) => (
          <NavigationButton
            key={item.label}
            leftIcon={item.iconName}
            rightIcon="chevron-right"
            text={item.label}
            onClick={() => navigate(item.path)}
          />
        ))}
      </div>

      {/* Delete Account (only for logged-in member) */}
      {displayMember?.id === member?.id && (
        <div className="mt-[64px]">
          <NavigationButton
            key="delete"
            variant="delete"
            leftIcon="Trash"
            text={t("basicInfo.deleteAccount")}
            onClick={handleDeleteAccount}
          />
        </div>
      )}
    </div>
  );
};

export default BasicInformation;
